import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    ReadableApexAccountRegistrationType,
    type SupportedApexAccountRegistrationType,
} from '@fruitful/types';
import React from 'react';
import {
    BodyMediumRegular,
    BodySmallRegular,
    FruitfulCustomIcon,
    hitSlop,
    Icon,
    InfoButtonWithTooltip,
    InstitutionLogo,
    isEmpty,
    redactPrivate,
    SquarePenIcon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import {
    type AccountSummary,
    type BankAccountDisplayDetail,
    type DepositAccount,
    type IncomeSplitReviewContext,
    type IncomeSplitRule,
    IncomeSplitRuleType,
    type PaycheckRoutingRuleAccountMetadata,
    PaycheckRoutingRuleAccountType,
    type ProposedAccount,
} from '../types/IncomeSplit';
import {
    getAccountLogo,
    getStringAccountProperty,
} from '../utils/incomeSplitAccountUtils';
import { calculateFinalizedAmountNew } from '../utils/incomeSplitUtilsNew';

const getLastFour = (accountNumber: string | null | undefined) => {
    const trimmedAccountNumber = accountNumber?.trim();
    if (trimmedAccountNumber == null || trimmedAccountNumber.length === 0) {
        return null;
    }

    return trimmedAccountNumber.slice(-4);
};

const formatAccountNameWithLastFour = (
    accountName: string,
    accountNumber: string | null | undefined,
) => {
    const lastFour = getLastFour(accountNumber);
    return lastFour == null ? accountName : `${accountName} (${lastFour})`;
};

const isSupportedApexAccountRegistrationType = (
    accountType: string,
): accountType is SupportedApexAccountRegistrationType =>
    accountType in ReadableApexAccountRegistrationType;

const getConditionRows = (
    rule: IncomeSplitRule,
): Array<{ label: string; value: string }> => {
    const {
        type,
        targetBalanceCents,
        monthlyContributionMaxCents,
        yearlyContributionMaxCents,
        retirementContributionLimit,
    } = rule;

    // Show "available balance" when type is MINIMUM_BALANCE *or* leftover
    // targetBalanceCents > 0. Generated splits usually convert BILLS to a
    // monthly cap (no target); a stale target here would mislabel the card.
    const isMinimumBalanceRule =
        type === IncomeSplitRuleType.MINIMUM_BALANCE ||
        (targetBalanceCents ?? 0) > 0;
    const hasMonthlyMax = (monthlyContributionMaxCents ?? 0) > 0;
    const hasYearlyMax = (yearlyContributionMaxCents ?? 0) > 0;
    const hasRetirementLimit = (retirementContributionLimit ?? 0) > 0;

    const rows: Array<{ label: string; value: string }> = [];

    if (isMinimumBalanceRule) {
        rows.push({
            label: 'until available balance =',
            value: centsToFormattedDollars(targetBalanceCents ?? 0, false),
        });
    }
    if (hasMonthlyMax) {
        rows.push({
            label: 'until monthly contribution =',
            value: centsToFormattedDollars(
                monthlyContributionMaxCents ?? 0,
                false,
            ),
        });
    }
    if (hasYearlyMax) {
        rows.push({
            label: 'until annual contribution =',
            value: centsToFormattedDollars(
                yearlyContributionMaxCents ?? 0,
                false,
            ),
        });
    }
    if (hasRetirementLimit) {
        rows.push({ label: 'until annual IRA limit', value: '' });
    }

    return rows;
};

export const IncomeSplitRuleCardNew = ({
    rule,
    account,
    color,
    icon,
    context,
    setSelectedRule,
    onPressEdit,
    isBackdoorTrad = false,
}: {
    rule: IncomeSplitRule;
    account:
        | PaycheckRoutingRuleAccountMetadata
        | BankAccountDisplayDetail
        | DepositAccount
        | ProposedAccount
        | AccountSummary;
    color?: string | null;
    icon?: string | null;
    context: IncomeSplitReviewContext;
    setSelectedRule?: (rule: IncomeSplitRule) => void;
    onPressEdit?: () => void;
    isBackdoorTrad?: boolean;
}) => {
    const isExternal =
        rule.accountType !== undefined &&
        rule.accountType === PaycheckRoutingRuleAccountType.EXTERNAL;

    const isInternalInvestment =
        rule.accountType !== undefined &&
        rule.accountType === PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT;

    let accountName: string;
    let accountTypeNumber: string | null = null;

    if (isExternal) {
        accountName = formatAccountNameWithLastFour(
            getStringAccountProperty(account, 'nickname') ??
                getStringAccountProperty(account, 'name') ??
                'External Account',
            getStringAccountProperty(account, 'accountNumberMasked') ??
                getStringAccountProperty(account, 'accountNumber') ??
                null,
        );
        accountTypeNumber =
            getStringAccountProperty(account, 'institutionName') ??
            getStringAccountProperty(account, 'bankName') ??
            null;
    } else if (isInternalInvestment) {
        const nickname =
            getStringAccountProperty(account, 'nickname') ??
            'Investment Account';
        const rawAccountType = getStringAccountProperty(account, 'accountType');
        const accountType =
            rawAccountType != null &&
            isSupportedApexAccountRegistrationType(rawAccountType)
                ? ReadableApexAccountRegistrationType[rawAccountType]
                : null;
        const accountNumber = getStringAccountProperty(
            account,
            'accountNumber',
        );

        accountName = nickname;

        // Format account type and number for smaller display
        if (!isEmpty(accountType) && !isEmpty(accountNumber)) {
            accountTypeNumber = `${accountType} ${accountNumber}`;
        } else if (!isEmpty(accountType)) {
            accountTypeNumber = accountType;
        } else if (!isEmpty(accountNumber)) {
            accountTypeNumber = accountNumber ?? null;
        }
    } else {
        accountName =
            getStringAccountProperty(account, 'nickname') ??
            getStringAccountProperty(account, 'name') ??
            'Account';
    }

    const contributeDisplay = calculateFinalizedAmountNew(rule);
    const conditionRows = getConditionRows(rule);

    const accountIcon = isExternal ? (
        <InstitutionLogo size="xxs" logo={getAccountLogo(account)} />
    ) : (
        <FruitfulCustomIcon
            icon={icon}
            color={color}
            size="xxs"
            useAltColors={
                rule.accountType ===
                PaycheckRoutingRuleAccountType.INTERNAL_INVESTMENT
            }
        />
    );

    const isEdit = context === 'edit';
    const hasDescription = !isEmpty(rule.description);

    const showEditActions = isBackdoorTrad || isEdit;
    const editActions = showEditActions ? (
        <XStack alignItems="center">
            {isBackdoorTrad && (
                <InfoButtonWithTooltip tooltipContent="Contributions to this IRA will be promptly converted to the linked Roth IRA as part of a backdoor Roth conversion. Funds will not be invested while held in this account. Reach out to your Guide if you have questions." />
            )}
            {isEdit && (
                <View
                    alignItems="center"
                    justifyContent="center"
                    animation="button"
                    pressStyle={{ scale: 0.95 }}
                    onPress={() => {
                        if (setSelectedRule) setSelectedRule(rule);
                        if (onPressEdit) onPressEdit();
                    }}
                    cursor="pointer"
                    {...hitSlop(12)}
                >
                    <Icon icon={SquarePenIcon} size={16} color="$gray1200" />
                </View>
            )}
        </XStack>
    ) : null;

    const contributeRow = (
        <XStack gap={8} alignItems="center">
            <BodyMediumRegular>Contribute</BodyMediumRegular>
            <BodyMediumRegular color="$green900" {...redactPrivate()}>
                {contributeDisplay}
            </BodyMediumRegular>
        </XStack>
    );

    return (
        <YStack backgroundColor="$white" borderRadius={16} overflow="hidden">
            {hasDescription ? (
                <XStack
                    backgroundColor="$petalLight"
                    paddingHorizontal={16}
                    paddingVertical={12}
                    alignItems="center"
                    justifyContent="space-between"
                    gap={12}
                >
                    <BodySmallRegular color="$petalDarkest" flex={1}>
                        {rule.description}
                    </BodySmallRegular>
                    {editActions}
                </XStack>
            ) : null}
            <YStack padding={16} gap={12}>
                {hasDescription ? (
                    contributeRow
                ) : (
                    <XStack alignItems="center" justifyContent="space-between">
                        {contributeRow}
                        {editActions}
                    </XStack>
                )}

                <XStack gap={8} alignItems="center">
                    <BodyMediumRegular>to</BodyMediumRegular>
                    {accountIcon}
                    <BodyMediumRegular color="$green900" flex={1}>
                        {accountName}
                        {!isEmpty(accountTypeNumber) && (
                            <BodySmallRegular color="$gray1000">
                                {` - ${accountTypeNumber}`}
                            </BodySmallRegular>
                        )}
                    </BodyMediumRegular>
                </XStack>

                {conditionRows.map((row, index) => (
                    <XStack
                        key={row.label}
                        gap={8}
                        alignItems="center"
                        flexWrap="wrap"
                    >
                        <BodyMediumRegular flexShrink={1}>
                            {index > 0 ? `or ${row.label}` : row.label}
                        </BodyMediumRegular>
                        {row.value.length > 0 && (
                            <BodyMediumRegular
                                color="$green900"
                                {...redactPrivate()}
                            >
                                {row.value}
                            </BodyMediumRegular>
                        )}
                    </XStack>
                ))}
            </YStack>
        </YStack>
    );
};

export default IncomeSplitRuleCardNew;
